import {Trans} from '@lingui/macro';
import React, {ReactNode, useMemo} from 'react';

import {IconButton} from '../../../libs/shared/ui/src/components/buttons/iconButton';
import {UserThemeConsumer} from '../../../libs/shared/ui/src/theme/userThemeConsumer';
import {useAppDispatch} from '../../helpers/reduxHelpers';
import {CollapsedItemKeyPrefixesEnum} from '../../models/api/localPreferencesModel';
import {useExpandItem} from '../../store/data/localPreferences/useExpandItem';
import {navigateToLinkAction} from '../../store/stateless/navigationActions';
import {SettingsSidebarInfoBox} from '../common/settingsSidebarInfoBox';
import {StyledSettingsSidebar} from '../common/settingsWrapper';

/*
 * Props.
 */

export interface UseSettingsInfoBoxProps {
  settingsExpandedKey: CollapsedItemKeyPrefixesEnum;
  title: ReactNode;
  body: ReactNode;
  link?: string;
}

/*
 * Hook.
 */

export function useSettingsInfoBox(props: UseSettingsInfoBoxProps) {
  const {settingsExpandedKey, title, body, link} = props;
  const [isInfoBoxOpen, toggleInfoBox] = useExpandItem(`${settingsExpandedKey}infoBox`, true);
  const dispatch = useAppDispatch();

  const infoBoxIcon = useMemo(
    () => (
      <UserThemeConsumer>
        {(theme) => (
          <IconButton
            iconName="info"
            label={<Trans id="settings-toggle-info-box">Toggle info box</Trans>}
            onClick={toggleInfoBox}
            color={isInfoBoxOpen ? theme.greys.shade80 : theme.greys.shade70}
            activeColor={isInfoBoxOpen ? theme.greys.shade80 : theme.greys.shade70}
            hoverColor={theme.greys.shade80}
            hoverActiveColor={theme.greys.shade80}
            backgroundColor={isInfoBoxOpen ? theme.greys.shade30 : undefined}
            backgroundHoverColor={isInfoBoxOpen ? theme.greys.shade40 : theme.greys.shade30}
          />
        )}
      </UserThemeConsumer>
    ),
    [isInfoBoxOpen, toggleInfoBox],
  );

  const infoBox = useMemo(() => {
    if (!isInfoBoxOpen) {
      return null;
    }

    return (
      <StyledSettingsSidebar>
        <SettingsSidebarInfoBox
          title={title}
          body={body}
          footer={link && <Trans id="settings-infobox-footer">Learn more</Trans>}
          onFooterClick={link ? () => dispatch(navigateToLinkAction(link)) : undefined}
          onCloseClick={toggleInfoBox}
        />
      </StyledSettingsSidebar>
    );
  }, [body, dispatch, isInfoBoxOpen, link, title, toggleInfoBox]);

  return useMemo(
    () => ({
      infoBoxIcon,
      infoBox,
    }),
    [infoBox, infoBoxIcon],
  );
}
